import { useCallback, useEffect, useRef, useState } from "react";
import axios from "../../../api/axiosInstance";
import useStockOperationAccess from "../useStockOperationAccess";
import { isDirectConsumptionItem } from "../itemsFamiliesUi";

const baseUrl = "/api/purchasing/stock-requisitions";
const emptyForm = () => ({ id: null, requisition_code: "", status: "draft", source_location_id: "", delivery_date: "", notes: "", lines: [], approval_revision: 0, approval_cycle: 0, approval_history: [] });
const confirmationActions = ["request", "approve", "dispatch"];
const confirmationStatuses = ["confirmed", "approved", "fulfilled"];
const confirmationPlanError = "Unable to verify the actions for this requisition. Please try again.";

export default function useStockRequisitionEditor({ requisitionId, businessUnitId }) {
  const access = useStockOperationAccess(businessUnitId);
  const generation = useRef(0);
  const config = useCallback((action = "view") => access.requestConfig(action === "view" && !access.can("requisitions", "view", true) ? "dispatched" : "requisitions", action), [access]);
  const guardedRequest = useCallback(async (method, ...args) => {
    const version = generation.current;
    const response = await axios[method](...args);
    if (version !== generation.current || !(access.can("requisitions", "view", true) || access.can("dispatched", "view", true))) {
      throw new Error("The document changed or your access changed. Close and reopen it before continuing.");
    }
    return response;
  }, [access]);
  const [form, setForm] = useState(emptyForm);
  const [context, setContext] = useState({ locations: [], items: [], balances: {} });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [pending, setPending] = useState(0);
  const [hasSaved, setHasSaved] = useState(false);
  const [conflicted, setConflicted] = useState(false);
  const blocked = useRef(false);
  const activeRef = useRef(true);
  const current = useRef(form);
  const recordId = useRef(null);
  const savedLines = useRef(new Map());
  const queue = useRef(Promise.resolve());

  const update = (change) => {
    current.current = typeof change === "function" ? change(current.current) : change;
    setForm(current.current);
  };

  const revision = () => {
    if (!activeRef.current) throw new Error("The editor was closed.");
    return Number(current.current.approval_revision || 0);
  };
  // Acknowledge only server metadata: another edit may already be queued in the
  // visible form, so replacing its fields with an older response loses input.
  const acknowledge = (data) => {
    if (!activeRef.current || !access.can("requisitions", "view", true) || !data || typeof data !== "object") return;
    const metadata = Object.fromEntries(Object.entries(data).filter(([key]) =>
      key.startsWith("approval_") || key.startsWith("can_") || key.startsWith("return_") || key.startsWith("returned_") || key.startsWith("submitted_") || key === "status"));
    update((previous) => ({ ...previous, ...metadata }));
  };
  const applyDocument = (data) => {
    if (!activeRef.current) return;
    const next = { ...emptyForm(), ...data, source_location_id: data.source_location_id || "", delivery_date: (data.delivery_date || "").slice(0, 10), lines: data.lines || [] };
    recordId.current = next.id;
    savedLines.current = new Map(next.lines.map((line) => [String(line.item_id), line]));
    update(next);
  };

  useEffect(() => {
    let active = true;
    activeRef.current = true;
    generation.current += 1;
    blocked.current = false;
    recordId.current = null;
    savedLines.current.clear();
    current.current = emptyForm();
    setForm(current.current); setLoading(true); setLoadError(""); setSaveError(""); setConflicted(false); setPending(0);
    const load = async () => {
      try {
        const [catalog, detail] = await Promise.all([
          guardedRequest("get", `${baseUrl}/context`, config()),
          requisitionId ? guardedRequest("get", `${baseUrl}/${requisitionId}`, config()) : Promise.resolve(null),
        ]);
        if (!active) return;
        setContext({
          permissions: catalog.data?.permissions,
          locations: catalog.data?.locations || [],
          items: (catalog.data?.items || []).filter((item) => !isDirectConsumptionItem(item)),
          balances: catalog.data?.balances || {},
        });
        if (detail) {
          const next = { ...emptyForm(), ...detail.data, source_location_id: detail.data.source_location_id || "", delivery_date: (detail.data.delivery_date || "").slice(0, 10), lines: detail.data.lines || [] };
          recordId.current = next.id;
          savedLines.current = new Map(next.lines.map((line) => [String(line.item_id), line]));
          current.current = next;
          setForm(next);
        }
      } catch (error) {
        if (active) setLoadError("Error loading requisition. Check console.");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; activeRef.current = false; };
  }, [requisitionId, businessUnitId, config, guardedRequest]);

  // Serialize writes so rapid changes cannot save an older value last or create
  // another draft while the first POST is still pending.
  const enqueue = (operation) => {
    const version = generation.current;
    setPending((value) => value + 1);
    const task = queue.current.catch(() => {}).then(() => {
      if (!activeRef.current || version !== generation.current) throw new Error("The editor was closed.");
      if (blocked.current) throw new Error("This document changed. Close and reopen it before editing.");
      return operation();
    });
    queue.current = task;
    task.then(() => { if (activeRef.current && version === generation.current) setHasSaved(true); }, (error) => {
      if (!activeRef.current || version !== generation.current) return;
      if ([403, 409].includes(error.response?.status)) {
        blocked.current = true;
        setConflicted(true);
      }
      setSaveError(blocked.current ? "This document changed. Close and reopen it before editing." : error.response?.data?.error || "Error saving changes.");
    }).finally(() => { if (activeRef.current && version === generation.current) setPending((value) => value - 1); });
    return task;
  };

  const ensureDraft = async () => {
    if (!activeRef.current) throw new Error("The editor was closed.");
    if (recordId.current) return recordId.current;
    const result = await guardedRequest("post", baseUrl, { business_unit_id: businessUnitId, notes: current.current.notes, source_location_id: current.current.source_location_id || null }, config("create"));
    if (!activeRef.current || !access.can("requisitions", "create", true)) throw new Error("The editor was closed.");
    recordId.current = result.data.id;
    acknowledge(result.data);
    update((previous) => ({ ...previous, id: result.data.id, requisition_code: result.data.requisition_code, status: result.data.status || "draft" }));
    return result.data.id;
  };

  const saveLine = async (id, line) => {
    const saved = savedLines.current.get(String(line.item_id));
    if (saved?.id && Number(saved.quantity) === Number(line.quantity) && (saved.notes || "") === (line.notes || "")) return;
    const result = await guardedRequest("post", `${baseUrl}/lines/autosave`, { requisition_id: id, item_id: line.item_id, quantity: Number(line.quantity), notes: line.notes || null, approval_revision: revision() }, config("update"));
    if (!result.data?.id) throw new Error("Error saving changes.");
    acknowledge(result.data);
    savedLines.current.set(String(line.item_id), { ...line, id: result.data.id });
    update((previous) => ({ ...previous, lines: previous.lines.map((entry) => String(entry.item_id) === String(line.item_id) ? { ...entry, id: result.data.id } : entry) }));
  };

  const changeHeader = (field, value) => {
    if (!access.can("requisitions", "update", true) || (!recordId.current && !access.can("requisitions", "create", true))) return;
    update((previous) => ({ ...previous, [field]: value }));
    enqueue(async () => {
      const id = await ensureDraft();
      const result = await guardedRequest("patch", `${baseUrl}/${id}`, { [field]: field === "notes" ? value : value || null, approval_revision: revision() }, config("update"));
      acknowledge(result.data);
    });
  };

  const addItem = (item) => {
    if (!access.can("requisitions", "update", true) || (!recordId.current && !access.can("requisitions", "create", true))) return;
    if (current.current.lines.some((line) => String(line.item_id) === String(item.id))) return;
    const line = { item_id: item.id, item, quantity: 1 };
    update((previous) => ({ ...previous, lines: [...previous.lines, line] }));
    enqueue(async () => { await saveLine(await ensureDraft(), line); });
  };

  const changeQuantity = (itemId, quantity) => {
    if (!access.can("requisitions", "update", true) || (!recordId.current && !access.can("requisitions", "create", true))) return;
    update((previous) => ({ ...previous, lines: previous.lines.map((line) => String(line.item_id) === String(itemId) ? { ...line, quantity } : line) }));
    if (!Number.isFinite(Number(quantity)) || Number(quantity) <= 0) return;
    const line = current.current.lines.find((entry) => String(entry.item_id) === String(itemId));
    enqueue(async () => { await saveLine(await ensureDraft(), line); });
  };

  const removeItem = (itemId) => {
    if (!access.can("requisitions", "update", true) || (!recordId.current && !access.can("requisitions", "create", true))) return;
    update((previous) => ({ ...previous, lines: previous.lines.filter((line) => String(line.item_id) !== String(itemId)) }));
    enqueue(async () => {
      const line = savedLines.current.get(String(itemId));
      if (line?.id) acknowledge((await guardedRequest("delete", `${baseUrl}/lines/${line.id}`, { ...config("update"), params: { business_unit_id: businessUnitId, approval_revision: revision() } })).data);
      savedLines.current.delete(String(itemId));
    });
  };

  // Reconcile the visible form before close/confirm and on retry, including any
  // autosave that failed while the user continued editing.
  const persist = (status) => {
    const snapshot = { ...current.current, lines: [...current.current.lines] };
    return enqueue(async () => {
      const id = await ensureDraft();
      for (const [itemId, line] of savedLines.current) {
        if (!snapshot.lines.some((entry) => String(entry.item_id) === itemId)) {
          acknowledge((await guardedRequest("delete", `${baseUrl}/lines/${line.id}`, { ...config("update"), params: { business_unit_id: businessUnitId, approval_revision: revision() } })).data);
          savedLines.current.delete(itemId);
        }
      }
      for (const line of snapshot.lines) await saveLine(id, line);
      const result = await guardedRequest("patch", `${baseUrl}/${id}`, {
        ...(snapshot.source_location_id ? { source_location_id: snapshot.source_location_id } : {}),
        delivery_date: snapshot.delivery_date || null,
        notes: snapshot.notes,
        ...(status ? { status } : {}),
        approval_revision: revision(),
      }, config("update"));
      acknowledge(result.data);
      if (status) applyDocument((await guardedRequest("get", `${baseUrl}/${id}`, config())).data);
      setSaveError("");
    });
  };

  const removeDraft = () => enqueue(async () => {
    if (recordId.current) await guardedRequest("delete", `${baseUrl}/${recordId.current}`, { ...config("delete"), params: { business_unit_id: businessUnitId, approval_revision: revision() } });
    recordId.current = null;
    savedLines.current.clear();
    setSaveError("");
  });

  // Read the authoritative plan only after the visible draft has been persisted.
  // Confirmation uses this exact revision and action list without another save.
  const getConfirmationPlan = () => enqueue(async () => {
    if (!recordId.current) throw new Error(confirmationPlanError);
    const result = await guardedRequest("post", `${baseUrl}/${recordId.current}/confirmation-plan`, { approval_revision: revision() }, config("submit"));
    if (!activeRef.current) throw new Error("The editor was closed.");
    const plan = result.data;
    if (!Array.isArray(plan?.actions) || !plan.actions.length || plan.actions.length > confirmationActions.length
      || plan.actions.some((action, index) => action !== confirmationActions[index])
      || plan.final_status !== confirmationStatuses[plan.actions.length - 1]
      || !Number.isSafeInteger(plan.approval_revision) || plan.approval_revision !== revision()) {
      throw new Error(confirmationPlanError);
    }
    return plan;
  });

  const confirm = (plan) => enqueue(async () => {
    if (!recordId.current || revision() !== plan.approval_revision) throw new Error("This document changed. Close and reopen it before editing.");
    if ((plan.actions.includes("approve") && !access.can("requisitions", "approve", true)) ||
      (plan.actions.includes("dispatch") && (!access.can("dispatched", "dispatch", true) || !access.can("dispatched", "finalize", true)))) {
      throw new Error("This document changed. Close and reopen it before editing.");
    }
    const result = await guardedRequest("patch", `${baseUrl}/${recordId.current}`, {
      status: "confirmed",
      process_authorized: true,
      expected_actions: plan.actions,
      approval_revision: plan.approval_revision,
    }, config("submit"));
    if (!activeRef.current) return;
    applyDocument(result.data);
    setSaveError("");
  });

  // Decisions use the version after any queued saves; the server verifies both
  // the state and ownership before recording the next history event.
  const decide = (action, comments) => enqueue(async () => {
    if (!recordId.current) throw new Error("Error approving requisition.");
    const roleAction = action === "recall" ? "recall" : action === "resubmit" ? "submit" : "approve";
    const result = action === "recall"
      ? await guardedRequest("patch", `${baseUrl}/${recordId.current}`, { status: "draft", approval_revision: revision() }, config(roleAction))
      : await guardedRequest("post", `${baseUrl}/${recordId.current}/${action}`, {
        approval_revision: revision(),
        ...(action === "return" ? { comments } : action === "reject" ? { reason: comments } : {}),
      }, config(roleAction));
    acknowledge(result.data);
    applyDocument((await guardedRequest("get", `${baseUrl}/${recordId.current}`, config())).data);
    setSaveError("");
  });

  return { access, form, context, loading, loadError, saveError, pending, hasSaved, conflicted, changeHeader, addItem, changeQuantity, removeItem, persist, getConfirmationPlan, confirm, isActive: () => activeRef.current && (access.can("requisitions", "view", true) || access.can("dispatched", "view", true)), decide, approve: () => decide("approve"), removeDraft };
}
